import { Component, OnDestroy, OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { PaginatedResponseInterface } from '@sport-activities/sdk';
import { Observable, of, Subscription } from 'rxjs';
import { map, mergeMap } from 'rxjs/operators';

import { appConfig } from '~/app.config';
import { PaginationTypeEnum } from '~/attendee/components/shared/pagination/enums/pagination-type.enum';
import { Advice } from '~/attendee/model/advice.model';
import { AdviceService } from '~/attendee/service/advice.service';
import {
  ResultPageFilters,
  ResultPageFilterService,
} from '~/attendee/service/result-page-filter.service';
import { VtmnButtonVariantEnum } from '~/component/buttons/vtmn-button/enum/vtmn-button-variant.enum';
import { TipsVariantEnum } from '~/component/tips/enum/variant.enum';
import { VtmnPlayIconTypeEnum } from '~/component/vtmn-play-icon/enums/vtmn-play-icon-type.enum';
import { CustomHeadTagService } from '~/core/services/custom-head-tag/custom-head-tag.service';
import { LoaderService } from '~/core/services/loader/loader.service';
import { MatchMediaService } from '~/core/services/match-media/match-media.service';
import { SportsService } from '~/core/services/sports/sports.service';
import { ToggleNameEnum } from '~/toggles/services/toggle/enums/name.enum';

const LOADER_KEY = 'loadAdvices';

@Component({
  selector: 'app-attendee-result-advice',
  templateUrl: './advice.component.html',
})
export class ResultAdviceComponent implements OnInit, OnDestroy {
  private readonly subscriptions = new Subscription();

  public readonly iconTypeEnum = VtmnPlayIconTypeEnum;
  public readonly paginationType = PaginationTypeEnum;
  public readonly tipsVariant = TipsVariantEnum;
  public readonly vtmnVariantEnum = VtmnButtonVariantEnum;

  private paramFilters: ResultPageFilters;

  public advices: Advice[];
  public currentPage = 1;
  public hasLoadMoreButton: boolean;
  public isLoading = false;
  public isMobileDevice: boolean;
  public toggleNameEnum = ToggleNameEnum;

  constructor(
    private readonly adviceService: AdviceService,
    private readonly resultPageFilterService: ResultPageFilterService,
    private readonly sportsService: SportsService,
    private readonly loaderService: LoaderService,
    private readonly translateService: TranslateService,
    private readonly customHeadTag: CustomHeadTagService,
    private readonly matchMediaService: MatchMediaService
  ) {}

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.customHeadTag.destroyMicrodata();
  }

  public ngOnInit(): void {
    this.subscriptions.add(
      this.resultPageFilterService.filters$
        .pipe(
          mergeMap((paramFilters) => {
            this.loaderService.start(LOADER_KEY);
            return this.loadAdvices(paramFilters).pipe(
              map((advices) => ({
                advices,
                paramFilters,
              }))
            );
          })
        )
        .subscribe(({ advices, paramFilters }) => {
          if (paramFilters.page === 1) {
            this.loaderService.stop(LOADER_KEY);
            this.advices = [];
          }

          this.paramFilters = paramFilters;
          this.advices = this.advices?.concat(advices?.items);
          this.hasLoadMoreButton = this.advices.length < advices?.total;
        })
    );

    this.subscriptions.add(
      this.loaderService.loading$.subscribe(() => {
        this.isLoading = this.loaderService.isLoading(LOADER_KEY); // loader off
      })
    );

    this.subscriptions.add(
      this.matchMediaService.isMobileDevice().subscribe((isMobileDevice) => {
        this.isMobileDevice = isMobileDevice;
      })
    );

    this.setBreadcrumb();
  }

  public get tips(): string[] {
    return this.translateService
      .instant('attendee.result_page.advice_tips.text')
      .split('✓');
  }

  private loadAdvices(
    filters: ResultPageFilters
  ): Observable<PaginatedResponseInterface<Advice>> {
    const { sportGroups } = filters;

    let baseObs$ = this.sportsService.getSportsGroups({
      identifiers: Array.isArray(sportGroups) ? sportGroups : [sportGroups],
    });

    if (!sportGroups) {
      baseObs$ = of([]);
    }

    // get sport by sport groups
    return baseObs$.pipe(
      mergeMap((groups) => {
        // flatten to array of sports
        const sportsByGroup = groups.reduce(
          (accumulator, currentValue) =>
            accumulator?.concat(currentValue?.sports),
          []
        );

        // retrieve advices
        return this.adviceService.getAdviceContents({
          dd_sports: sportsByGroup.join(','), // array values are expected with ,
          page: filters.page || 1,
          items_per_page: this.isMobileDevice
            ? appConfig.pagination.loadMore.mobile
            : appConfig.pagination.loadMore.desktop,
        });
      })
    );
  }

  private setBreadcrumb(): void {
    this.customHeadTag.setBreadcrumb([
      this.customHeadTag.newBreadcrumItem(
        this.translateService.instant('seo.breadcrumb.item.advice')
      ),
    ]);
  }

  public loadMore(): void {
    this.paramFilters.page += 1;
    this.resultPageFilterService.filters$.next(this.paramFilters);
  }

  public resetFilters(): void {
    this.resultPageFilterService.resetFilters();
  }
}
